import React, { useState } from 'react';
import toast from 'react-hot-toast';
import { useTheme } from '../../contexts/ThemeContext';
import { useAuth } from '../../contexts/AuthContext';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';

import KnowledgeBaseSearch from './components/KnowledgeBaseSearch';
import FAQSection from './components/FAQSection';
import VideoTutorials from './components/VideoTutorials';
import TicketSubmissionForm from './components/TicketSubmissionForm';
import TicketTrackingDashboard from './components/TicketTrackingDashboard';
import LiveChatWidget from './components/LiveChatWidget';

const SupportTicketSystem = () => {
  const { isDarkMode } = useTheme();
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('knowledge-base');
  const [showTicketForm, setShowTicketForm] = useState(false);
  const [showLiveChat, setShowLiveChat] = useState(false);

  const tabs = [
    { id: 'knowledge-base', label: 'Knowledge Base', icon: 'BookOpen' },
    { id: 'faq', label: 'FAQ', icon: 'HelpCircle' },
    { id: 'tutorials', label: 'Video Tutorials', icon: 'Video' },
    { id: 'my-tickets', label: 'My Tickets', icon: 'Ticket' }
  ];

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <div className={`border-b ${
        isDarkMode 
          ? 'bg-[#0a0f0d] border-[#1a2820]' 
          : 'bg-white border-gray-200'
      }`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <h1 className="text-2xl md:text-3xl font-bold text-foreground mb-2">
                Help Center
              </h1>
              <p className="text-sm md:text-base text-muted-foreground">
                Find answers, submit tickets, and get support
              </p>
            </div>
            <div className="flex flex-wrap gap-2">
              <Button
                variant="outline"
                size="default"
                iconName="MessageCircle"
                onClick={() => setShowLiveChat(true)}
              >
                Live Chat
              </Button>
              <Button
                variant="default"
                size="default"
                iconName="Plus"
                onClick={() => setShowTicketForm(true)}
              >
                Submit Ticket
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs Navigation */}
      <div className={`border-b ${
        isDarkMode 
          ? 'bg-[#0a0f0d] border-[#1a2820]' 
          : 'bg-gray-50 border-gray-200'
      }`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex overflow-x-auto">
            {tabs?.map((tab) => (
              <button
                key={tab?.id}
                onClick={() => setActiveTab(tab?.id)}
                className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                  activeTab === tab?.id
                    ? isDarkMode
                      ? 'border-[#10b981] text-[#10b981]'
                      : 'border-[#107570] text-[#107570]' :'border-transparent text-muted-foreground hover:text-foreground'
                }`}
              >
                <Icon name={tab?.icon} size={18} />
                {tab?.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {activeTab === 'knowledge-base' && <KnowledgeBaseSearch />}
        {activeTab === 'faq' && <FAQSection />}
        {activeTab === 'tutorials' && <VideoTutorials />}
        {activeTab === 'my-tickets' && <TicketTrackingDashboard />}
      </div>

      {/* Ticket Submission Modal */}
      {showTicketForm && (
        <TicketSubmissionForm
          onClose={() => setShowTicketForm(false)}
          onSuccess={(ticketRef) => {
            setShowTicketForm(false);
            setActiveTab('my-tickets');
            toast.success(`Ticket ${ticketRef || ''} submitted — we'll respond within one business day.`, { duration: 6000 });
          }}
        />
      )}

      {/* Live Chat Widget */}
      {showLiveChat && (
        <LiveChatWidget onClose={() => setShowLiveChat(false)} />
      )}
    </div>
  );
};

export default SupportTicketSystem;